import { useReducer, useEffect, useCallback } from 'react';
import { useBindingService } from './services/bindingService';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { extractErrorMessage } from '../../../shared/utils/errorUtils';
import type { VersionParameter, CfnParameterBinding, ResolverEntry } from './types';

export type BindingEditorType = 'literal' | 'resolver' | 'operator-input' | 'deployment-output';

// Draft mirrors CfnParameterBinding but uses source_component (UI name) instead of source_product
export type BindingDraft =
  | { type: 'literal'; value: string }
  | { type: 'resolver'; resolver_id: string }
  | { type: 'operator-input'; required: boolean }
  | { type: 'deployment-output'; source_component: string; output_key: string };

interface State {
  parameters: VersionParameter[];
  draft: Record<string, BindingDraft>;
  resolvers: ResolverEntry[];
  loading: boolean;
  resolversLoading: boolean;
  submitting: boolean;
  bindingsComplete: boolean;
  unboundCount: number;
  dirty: boolean;
}

type Action =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; parameters: VersionParameter[]; bindingsComplete: boolean; unboundCount: number }
  | { type: 'LOAD_FAIL' }
  | { type: 'RESOLVERS_SUCCESS'; resolvers: ResolverEntry[] }
  | { type: 'SET_TYPE'; key: string; bindingType: BindingEditorType }
  | { type: 'SET_LITERAL'; key: string; value: string }
  | { type: 'SET_RESOLVER'; key: string; resolverId: string }
  | { type: 'SET_REQUIRED'; key: string; required: boolean }
  | { type: 'SET_DEPLOYMENT_OUTPUT'; key: string; field: 'source_component' | 'output_key'; value: string }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_SUCCESS'; parameters: VersionParameter[]; bindingsComplete: boolean; unboundCount: number }
  | { type: 'SUBMIT_FAIL' };

function bindingToDraft(binding: CfnParameterBinding | null): BindingDraft {
  if (!binding) return { type: 'literal', value: '' };
  switch (binding.type) {
    case 'literal': return { type: 'literal', value: binding.value };
    case 'resolver': return { type: 'resolver', resolver_id: binding.resolver_id };
    case 'operator-input': return { type: 'operator-input', required: binding.required };
    case 'deployment-output': return { type: 'deployment-output', source_component: binding.source_product, output_key: binding.output_key };
  }
}

function draftToBinding(draft: BindingDraft): CfnParameterBinding {
  switch (draft.type) {
    case 'literal': return { type: 'literal', value: draft.value };
    case 'resolver': return { type: 'resolver', resolver_id: draft.resolver_id };
    case 'operator-input': return { type: 'operator-input', required: draft.required };
    case 'deployment-output': return { type: 'deployment-output', source_product: draft.source_component, output_key: draft.output_key };
  }
}

function buildDraft(parameters: VersionParameter[]): Record<string, BindingDraft> {
  const draft: Record<string, BindingDraft> = {};
  for (const p of parameters) {
    draft[p.cfn_key] = bindingToDraft(p.current_binding);
  }
  return draft;
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, loading: true };
    case 'LOAD_SUCCESS':
      return { ...state, loading: false, parameters: action.parameters, draft: buildDraft(action.parameters), bindingsComplete: action.bindingsComplete, unboundCount: action.unboundCount, dirty: false };
    case 'LOAD_FAIL':
      return { ...state, loading: false };
    case 'RESOLVERS_SUCCESS':
      return { ...state, resolvers: action.resolvers, resolversLoading: false };
    case 'SET_TYPE': {
      const prev = state.draft[action.key];
      let next: BindingDraft;
      switch (action.bindingType) {
        case 'literal': next = { type: 'literal', value: prev?.type === 'literal' ? prev.value : '' }; break;
        case 'resolver': next = { type: 'resolver', resolver_id: prev?.type === 'resolver' ? prev.resolver_id : '' }; break;
        case 'operator-input': next = { type: 'operator-input', required: prev?.type === 'operator-input' ? prev.required : false }; break;
        case 'deployment-output': next = { type: 'deployment-output', source_component: '', output_key: '' }; break;
      }
      return { ...state, draft: { ...state.draft, [action.key]: next }, dirty: true };
    }
    case 'SET_LITERAL':
      return { ...state, draft: { ...state.draft, [action.key]: { type: 'literal', value: action.value } }, dirty: true };
    case 'SET_RESOLVER':
      return { ...state, draft: { ...state.draft, [action.key]: { type: 'resolver', resolver_id: action.resolverId } }, dirty: true };
    case 'SET_REQUIRED': {
      const cur = state.draft[action.key];
      return { ...state, draft: { ...state.draft, [action.key]: { type: 'operator-input', required: action.required, ...(cur?.type === 'operator-input' ? {} : {}) } }, dirty: true };
    }
    case 'SET_DEPLOYMENT_OUTPUT': {
      const cur = state.draft[action.key];
      const base: BindingDraft = cur?.type === 'deployment-output' ? cur : { type: 'deployment-output', source_component: '', output_key: '' };
      return { ...state, draft: { ...state.draft, [action.key]: { ...base, [action.field]: action.value } as BindingDraft }, dirty: true };
    }
    case 'SUBMIT_START':
      return { ...state, submitting: true };
    case 'SUBMIT_SUCCESS':
      return { ...state, submitting: false, parameters: action.parameters, draft: buildDraft(action.parameters), bindingsComplete: action.bindingsComplete, unboundCount: action.unboundCount, dirty: false };
    case 'SUBMIT_FAIL':
      return { ...state, submitting: false };
    default:
      return state;
  }
}

function initialState(): State {
  return { parameters: [], draft: {}, resolvers: [], loading: true, resolversLoading: true, submitting: false, bindingsComplete: false, unboundCount: 0, dirty: false };
}

interface UseBindingEditorOptions {
  service: string;
  productName: string;
  versionName: string;
}

export function useBindingEditor({ service, productName, versionName }: UseBindingEditorOptions) {
  const bindingService = useBindingService();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [state, dispatch] = useReducer(reducer, undefined, initialState);

  useEffect(() => {
    let mounted = true;
    dispatch({ type: 'LOAD_START' });
    bindingService.getVersionParameters(service, productName, versionName)
      .then((res) => {
        if (mounted) dispatch({
          type: 'LOAD_SUCCESS',
          parameters: res.parameters,
          bindingsComplete: res.parameters.every((p) => p.current_binding !== null),
          unboundCount: res.parameters.filter((p) => p.current_binding === null).length,
        });
      })
      .catch(() => { if (mounted) dispatch({ type: 'LOAD_FAIL' }); });
    return () => { mounted = false; };
  }, [service, productName, versionName, bindingService]);

  useEffect(() => {
    let mounted = true;
    bindingService.listResolvers()
      .then((res) => { if (mounted) dispatch({ type: 'RESOLVERS_SUCCESS', resolvers: res.resolvers }); })
      .catch(() => { if (mounted) dispatch({ type: 'RESOLVERS_SUCCESS', resolvers: [] }); });
    return () => { mounted = false; };
  }, [bindingService]);

  const setType = useCallback((key: string, bindingType: BindingEditorType) => {
    dispatch({ type: 'SET_TYPE', key, bindingType });
  }, []);

  const setLiteral = useCallback((key: string, value: string) => {
    dispatch({ type: 'SET_LITERAL', key, value });
  }, []);

  const setResolver = useCallback((key: string, resolverId: string) => {
    dispatch({ type: 'SET_RESOLVER', key, resolverId });
  }, []);

  const setRequired = useCallback((key: string, required: boolean) => {
    dispatch({ type: 'SET_REQUIRED', key, required });
  }, []);

  const setDeploymentOutput = useCallback((key: string, field: 'source_component' | 'output_key', value: string) => {
    dispatch({ type: 'SET_DEPLOYMENT_OUTPUT', key, field, value });
  }, []);

  const save = useCallback(async () => {
    dispatch({ type: 'SUBMIT_START' });
    const bindings: Record<string, CfnParameterBinding> = {};
    for (const [key, d] of Object.entries(state.draft)) {
      bindings[key] = draftToBinding(d);
    }
    try {
      const res = await bindingService.setVersionBindings(service, productName, versionName, bindings);
      dispatch({ type: 'SUBMIT_SUCCESS', parameters: res.parameters, bindingsComplete: res.bindings_complete, unboundCount: res.unbound_count });
      showSnackbar(res.bindings_complete ? 'Bindings saved — version is ready' : `Bindings saved — ${res.unbound_count} still unbound`);
    } catch (err) {
      dispatch({ type: 'SUBMIT_FAIL' });
      showSnackbar(extractErrorMessage(err, 'Failed to save bindings'), 'error');
    }
  }, [bindingService, service, productName, versionName, state.draft, showSnackbar]);

  return {
    parameters: state.parameters,
    draft: state.draft,
    resolvers: state.resolvers,
    loading: state.loading,
    resolversLoading: state.resolversLoading,
    submitting: state.submitting,
    bindingsComplete: state.bindingsComplete,
    unboundCount: state.unboundCount,
    dirty: state.dirty,
    setType,
    setLiteral,
    setResolver,
    setRequired,
    setDeploymentOutput,
    save,
    snackbar,
    closeSnackbar,
  };
}
